import React, { PropTypes, Component } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router'
import { reduxForm } from 'redux-form'
import Cookies from 'js-cookie'
import Helmet from 'react-helmet'
import ProductModal from 'components/products/ProductModal'

// Actions
import { bannerActions, companyActions, productActions, userActions } from 'redux/actionCreators'

// Styles
import classes from './App.scss'

// Lodash
import { get, debounce } from 'lodash'
import { iOSSafari, freezeScreen, unfreezeScreen } from 'helpers/utilities'
import { validateRequired, emailIsValid, hasError, inputProps } from 'helpers/formHelpers'

const hasToken = Cookies.get('token')
const mapStateToProps = (state, ownProps) => ({
  me: get(state, ['user', 'me']),
  statusCode: get(state, ['user', 'error', 'status']),
  loginError: get(state, ['user', 'loginError']) || false,
  viewportWidth: get(state, 'app.viewportWidth')
})
const mapDispatchToProps = (dispatch) => ({
  attemptVerify: () =>
    dispatch(userActions.attemptVerify()),
  attemptLogin: (data) =>
    dispatch(userActions.attemptLogin(data)),
  attemptFindBanners: () =>
    dispatch(bannerActions.attemptFindBanners()),
  attemptFindTags: () =>
    dispatch(productActions.attemptFindTags()),
  attemptFindCompanyOptions: () =>
    dispatch(companyActions.attemptFindCompanyOptions()),
  setInitial: () => dispatch({ type: 'SET_INITIAL' })
})

const FIELD_NAMES = [{ key: 'email', text: 'อีเมล' }, { key: 'password', text: 'รหัสผ่าน' }]
@connect(mapStateToProps, mapDispatchToProps)
@reduxForm({ form: 'Login', fields: FIELD_NAMES.map(field => field.key) })
class App extends Component {
  static propTypes = {
    me: PropTypes.object,
    loginError: PropTypes.bool.isRequired,

    attemptVerify: PropTypes.func.isRequired,
    attemptLogin: PropTypes.func.isRequired,
    attemptFindBanners: PropTypes.func.isRequired,
    attemptFindTags: PropTypes.func.isRequired,
    attemptFindCompanyOptions: PropTypes.func.isRequired
  };

  static contextTypes = {
    router: PropTypes.object.isRequired
  };

  constructor (props) {
    super(props)
    const unauthorized = !hasToken && !this._isExceptedPath(props)
    if (unauthorized && get(props, 'location.pathname' !== '/')) {
      this.context.router.replace('/')
    }
    this.state = {
      remembered: false,
      freezed: unauthorized
    }
    this._iOSSafari = iOSSafari()
  }

  componentDidMount () {
    window.addEventListener('resize', this._handleResize)
    this._handleResize()

    window.objectFitImages()
    this.props.attemptVerify()
    this.props.attemptFindBanners()
    this.props.attemptFindTags()
    this.props.attemptFindCompanyOptions()
    this._freezeIf(this.state.freezed)
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this._handleResize)
  }

  _handleResize = debounce(() => {
    this.props.dispatch({
      type: 'WINDOW_RESIZE',
      viewportWidth: (typeof document === 'undefined' || typeof window === 'undefined')
        ? 0 : Math.max(document.documentElement.clientWidth, window.innerWidth || 0),
      viewportHeight: (typeof document === 'undefined' || typeof window === 'undefined')
        ? 0 : Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
    })
  }, 250)

  componentWillReceiveProps (nextProps) {
    if (this.props.location.pathname !== nextProps.location.pathname) {
      let freezed = !this._isExceptedPath(nextProps)
      freezed = freezed && !get(nextProps, ['me', 'userId'])
      this._freezeIf(freezed)
      // if (freezed) this.context.router.replace('/');
      this.setState({ freezed: freezed })
    } else if (this.props.me !== nextProps.me) {
      let freezed = !get(nextProps, ['me', 'userId'])
      this._freezeIf(freezed)
      // if (freezed) this.context.router.replace('/');

      if (get(nextProps, ['me', 'role']) === 'admin' &&
        (get(nextProps, ['location', 'pathname']) || '').indexOf('admin') === -1) {
        this.context.router.replace('/admin')
      }
      this.setState({ freezed: freezed })
    }
  }

  _freezeIf = (condition) => {
    if (condition) {
      freezeScreen()
    } else {
      unfreezeScreen()
    }
  }

  _isExceptedPath = (props) =>
    ['signup', 'forgot-password', 'set-new-password']
      .indexOf((get(props, ['location', 'pathname']) || '').replace('/', '')) > -1;

  _validateInput = () => {
    const { attempting, fields } = this.props
    let error = FIELD_NAMES.reduce((error, { key, text }) => ({
      ...error, ...validateRequired(fields[key].value, key, text)
    }), {})
    if (!error.emailError && !emailIsValid(fields.email.value)) {
      error = { ...error, emailError: 'รูปแบบอีเมล์ไม่ถูกต้อง' }
    }

    this.setState(error)
    return !attempting && !hasError(error)
  }

  _onEnter = (e) => {
    if (e.keyCode === 13) this._onLogin()
  }

  _onLogin = () => {
    const { attemptLogin, fields: { email, password } } = this.props
    if (this._validateInput()) {
      attemptLogin({ email: email.value, password: password.value })
    }
  }

  render () {
    const { me, statusCode, loginError, viewportWidth } = this.props
    const { email, password } = this.props.fields
    const isAdmin = get(me, 'role') === 'admin'
    return (
      <div className={`${classes.app} ${isAdmin && classes.admin}`}>
        <Helmet titleTemplate='ชัชชัยเภสัช | %s' />
        {viewportWidth >= 768 ? (
          <div className={classes.desktop}>
            <div className={classes.body}>{this.props.children}</div>
            {this.state.freezed && (
              <div className='overlay'>
                <div className='overlay-cell'>
                  <div className={classes.loginCard}>
                    <div className={classes.header}>ชัชชัยเภสัช</div>
                    <div className={classes.left}>
                      <div className={classes.welcome}>ยินดีต้อนรับ</div>
                      <div className={classes.input}>
                        <span>อีเมล</span>
                        <input type='text' onKeyDown={this._onEnter} {...inputProps(email)}
                          className={this.state.emailError && classes.error} />
                      </div>
                      <div className={classes.input}>
                        <span>รหัสผ่าน</span>
                        <input type='password' onKeyDown={this._onEnter} {...inputProps(password)}
                          className={this.state.passwordError && classes.error} />
                      </div>
                      <div className={classes.lastLine}>
                        {this.state.emailError && <div className={classes.error}>*{this.state.emailError}</div>}
                        {this.state.passwordError && <div className={classes.error}>*{this.state.passwordError}</div>}
                        {loginError && <div className={classes.error}>
                          {statusCode === 401
                            ? '*อีเมล์หรือรหัสผ่านไม่ถูกต้อง'
                            : '*อีเมล์/รหัสผ่านไม่ถูกต้อง หรือยังไม่ได้รับการอนุมัติค่ะ'}
                        </div>}
                      </div>
                      <div className={classes.buttonRow}>
                        <Link to='/forgot-password'>ลืมรหัสผ่าน ?</Link>
                        <div className={classes.button} onClick={this._onLogin}>
                          <span>เข้าสู่ระบบ</span>
                        </div>
                      </div>
                    </div>
                    <div className={classes.right}>
                      <Link to='/signup'>
                        <div className={classes.line1}>สำหรับสมาชิกใหม่</div>
                        <div className={classes.line2}>ลงทะเบียน</div>
                        <div className={classes.button}>คลิกที่นี่ค่ะ</div>
                      </Link>
                      <a href='https://www.facebook.com/chatchaibhaesaj' className={classes.facebookText}>
                        <div>
                          <span>รู้จักร้านเรามากขึ้นได้ที</span>
                          <img src='/images/icon-facebook.jpg' alt='icon-facebook' />
                        ่</div>
                        <div>หรือโทร. 063-464-6196</div>
                      </a>
                      <img className={classes.mascot} width='103' height='122' src='/images/mascot.png' alt='mascot' />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div>
            {this.state.freezed ? (
              <div className={classes.mobile}>
                <div className={classes.header}>
                  <img className='logo' src='/images/logo.png' alt='logo' />
                </div>
                <div className={classes.login}>
                  <div className={classes.loginWrapper}>
                    <div className={classes.welcome}>
                      <span>ยินดีต้อนรับ</span>
                    </div>
                    <div className={classes.input}>
                      <span>อีเมล</span>
                      <input type='text' onKeyDown={this._onEnter} {...inputProps(email)}
                        className={this.state.emailError && classes.error} />
                    </div>
                    <div className={classes.input}>
                      <span>รหัสผ่าน</span>
                      <input type='password' onKeyDown={this._onEnter} {...inputProps(password)}
                        className={this.state.passwordError && classes.error} />
                    </div>
                    <div className={classes.lastLine}>
                      {this.state.emailError && <div className={classes.error}>*{this.state.emailError}</div>}
                      {this.state.passwordError && <div className={classes.error}>*{this.state.passwordError}</div>}
                      {loginError && <div className={classes.error}>
                        {statusCode === 401
                          ? '*อีเมล์หรือรหัสผ่านไม่ถูกต้อง'
                          : '*อีเมล์/รหัสผ่านไม่ถูกต้อง หรือยังไม่ได้รับการอนุมัติค่ะ'}
                      </div>}
                    </div>
                    <div className={classes.buttonCol}>
                      <Link to='/forgot-password'>ลืมรหัสผ่าน ?</Link>
                      <div className={classes.button} onClick={this._onLogin}>
                        <span>เข้าสู่ระบบ</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={classes.toSignup}>
                  <Link to='/signup'>
                    <div className={classes.line1}>สำหรับสมาชิกใหม่</div>
                    <div className={classes.line2}>ลงทะเบียน</div>
                    <div className={classes.button}>คลิกที่นี่ค่ะ</div>
                  </Link>
                  <a href='https://www.facebook.com/chatchaibhaesaj' className={`${classes.facebookText} ${this._iOSSafari && classes.iOSSafari}`}>
                    <div className={classes.facebookTextRow}>
                      <span>รู้จักร้านเรามากขึ้นได้ที</span>
                      <img src='/images/icon-facebook.jpg' alt='icon-facebook' />
                    ่</div>
                    <div className={classes.facebookTextRow}>หรือโทร. 063-464-6196</div>
                  </a>
                </div>
              </div>
            ) : (
              <div className='mobile'>
                <div className={classes.body}>{this.props.children}</div>
              </div>
            )}
          </div>
        )}
        <ProductModal />
      </div>
    )
  }
}
export default App



/** WEBPACK FOOTER **
 ** ./src/containers/App.js
 **/